import { instance } from 'utils/axios.instance';
import {
  ICreateFileLinkBody,
  ICreateFileLinkResponse,
  ICreateMediaEntryBody,
  ICreateMediaEntryResponse,
  IGetProjectMediaArgs,
  IGetProjectMediaResponse,
  IPostMediaDeleteInfo,
  IResolvedMediaItem,
  IResolveFileCabinetIdsResponse,
} from 'redux-store/api/file-cabinet';
import { createLatestValueQueue, LatestValueJob } from 'utils/createLatestValueQueue';

/**
 * Imperative service helpers for the unified File Cabinet API.
 *
 * These wrap the existing axios instance so they can be called from
 * Jotai atoms (which don't have access to React's RTK Query hooks).
 * RTK Query hooks remain available for React components.
 */

const FILE_CABINET = '/file-cabinet';

export async function createMediaEntry(
  body: ICreateMediaEntryBody,
): Promise<ICreateMediaEntryResponse> {
  const { projectId, ...payload } = body;
  return instance.post(`${FILE_CABINET}/${projectId}/media-entry`, payload);
}

export async function deleteMediaEntry(
  projectId: string | number,
  entryId: number,
  contextId?: string,
  contextItemId?: string,
): Promise<void> {
  const params: string[] = [];
  if (contextId) {
    params.push(`contextId=${encodeURIComponent(contextId)}`);
  }
  if (contextItemId !== undefined) {
    params.push(`contextItemId=${encodeURIComponent(contextItemId)}`);
  }
  const query = params.length ? `?${params.join('&')}` : '';
  return instance.delete(
    `${FILE_CABINET}/${projectId}/media-entry/${entryId}${query}`,
  );
}

export async function getPostMediaDeleteInfo(
  projectId: string | number,
  contextId: string,
): Promise<IPostMediaDeleteInfo> {
  return instance.get(
    `${FILE_CABINET}/${projectId}/post-media-delete-info/${contextId}`,
  );
}

export async function deleteMediaEntriesByPost(
  projectId: string | number,
  contextId: string,
  mode: 'unlink' | 'delete' = 'delete',
): Promise<void> {
  return instance.delete(
    `${FILE_CABINET}/${projectId}/media-entries-by-post/${contextId}`,
    { data: { mode } },
  );
}

export async function deleteMediaEntries(
  projectId: string | number,
  entryIds: number[],
  contextId?: string,
): Promise<void> {
  if (!entryIds.length) return;
  return instance.delete(`${FILE_CABINET}/${projectId}/media-entries`, {
    data: contextId ? { entryIds, contextId } : { entryIds },
  });
}

export async function getProjectMedia(
  projectId: string | number,
  args: Omit<IGetProjectMediaArgs, 'projectId'> = {},
): Promise<IGetProjectMediaResponse> {
  const params = new URLSearchParams();
  params.set('page', String(args.page ?? 1));
  params.set('limit', String(args.limit ?? 50));

  if (args.fromDate) {
    const dateValue = args.fromDate instanceof Date ? args.fromDate.toISOString() : args.fromDate;
    params.set('fromDate', dateValue);
  }

  if (args.toDate) {
    const dateValue = args.toDate instanceof Date ? args.toDate.toISOString() : args.toDate;
    params.set('toDate', dateValue);
  }

  if (typeof args.phaseId === 'number') {
    params.set('phaseId', String(args.phaseId));
  }

  if (typeof args.scheduleItemId === 'number') {
    params.set('scheduleItemId', String(args.scheduleItemId));
  }

  if (typeof args.userId === 'number') {
    params.set('userId', String(args.userId));
  }

  if (args.type) {
    params.set('type', args.type);
  }

  if (args.tags?.length) {
    args.tags.forEach((tag) => params.append('tags', tag));
  }

  return instance.get(`${FILE_CABINET}/${projectId}/media?${params.toString()}`);
}

export async function resolveFileCabinetIds(
  projectId: string | number,
  ids: number[],
): Promise<IResolveFileCabinetIdsResponse> {
  if (ids.length === 0) {
    return { items: [] as IResolvedMediaItem[] };
  }
  return instance.post(`${FILE_CABINET}/${projectId}/resolve`, { ids });
}

export async function createFileLink(
  body: ICreateFileLinkBody,
): Promise<ICreateFileLinkResponse> {
  const { projectId, ...payload } = body;
  return instance.post(`${FILE_CABINET}/${projectId}/file-link`, payload);
}

export async function updateMediaEntryDescription(
  projectId: string | number,
  entryId: number,
  description: string | null,
): Promise<void> {
  return instance.patch(
    `${FILE_CABINET}/${projectId}/media-entry/${entryId}/description`,
    { description },
  );
}

interface MediaDescriptionUpdate {
  projectId: string | number;
  entryId: number;
  description: string | null;
}

const mediaDescriptionQueue = createLatestValueQueue<string, MediaDescriptionUpdate>(
  (_key, update) =>
    updateMediaEntryDescription(update.projectId, update.entryId, update.description),
);

export function queueMediaEntryDescriptionUpdate(
  update: MediaDescriptionUpdate,
  callbacks: Omit<LatestValueJob<MediaDescriptionUpdate>, 'value'> = {},
): void {
  mediaDescriptionQueue(`${update.projectId}:${update.entryId}`, {
    value: update,
    ...callbacks,
  });
}

export async function updateMediaEntryThumbnail(
  projectId: string | number,
  entryId: number,
  thumbnailUrl: string,
): Promise<void> {
  return instance.patch(
    `${FILE_CABINET}/${projectId}/media-entry/${entryId}/thumbnail`,
    { thumbnailUrl },
  );
}
